// TODO(post-experiment): 실험 #292(match/parlor-requests, 장례식장 견적) 결과 확정 후 본 파일 BaseStack 마이그레이션 필수.
// CLAUDE.md "신규 코드는 BaseStack 사용 필수" 룰을 실험 속도 우선으로 일시 위반한 영역.
// 결과가 좋든 나쁘든 7일 이내 정리. wiki/features/platform-match-request-auto-advance-ab.md 참조.
import { QuotationResponse } from "@/apis/sangjoQuotation";
import { isLoggedInState } from "@/atoms/accountState";
import { matchRequestResDataState } from "@/atoms/match/matchRequestResDataState";
import { quotationRequestState } from "@/atoms/quotationRequestState";
import { temporaryMatchRequestState } from "@/containers/MatchContainer/atoms";
import {
  장례준비긴급도_LIST,
  조문객예상인원_LIST,
} from "@/containers/MatchContainer/constants/request";
import { fireGuestSubmitEvents } from "@/containers/MatchContainer/fireGuestSubmitEvents";
import { ExpectedPriceRangeBar } from "@/containers/MatchContainer/components/MatchRequest/ExpectedPriceRangeBar";
import { useDialog } from "@/hooks/useDialog";
import useSangjoQuotationRequests from "@/hooks/useSangjoQuotationRequests";
import { ParlorDetailProps } from "@/types/parlor";
import { Box, Button, HStack, useDisclosure, VStack } from "@chakra-ui/react";
import { AxiosResponse } from "axios";
import {
  ColorIcon,
  colors,
  cooKeys,
  customEvent,
  Field,
  getCookie,
  HackleEventName,
  setCookie,
} from "goi_common";
import { HEADER_HEIGHT } from "@/constants/components";
import { armBackTrap } from "../hooks/useParlorRouterChangeLoad";
import { Axios } from "@/utils/axios";
import { useRouter } from "next/router";
import {
  Dispatch,
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";

// 위 비용 게이지/콘텐츠 애니메이션이 먼저 움직이게 100ms 더 지연 (300 → 400).
const PARLOR_AUTO_ADVANCE_DELAY_MS = 400;

const parlorSlideVariants = {
  enter: { x: 60, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -60, opacity: 0 },
};

export type ParlorRequestVariation = "A" | "B" | "C";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import RequestUniqueFixedButton from "./RequestUniqueFixedButton";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";
import { safeParlorIds } from "@/constants/parlor/excludeParlors";
import CompanyInfoBanner from "@/components/common/CompanyInfoBanner";
import { UrgentCallBannerExperiment } from "@/components/UrgentCallBannerExperiment";
import UrgentModal from "@/components/common/Modal/UrgentModal";
import { RequestQuestionUniqueStepButtons } from "@/containers/Parlor/ParlorRequestContainer/components/RequestQuestionUniqueStep";
import {
  buildBinsoOptions,
  recommendBinso,
  BinsoOption,
  BinsoRecommendation,
} from "@/containers/Parlor/ParlorRequestContainer/utils/recommendBinso";

/** 견적 문항 종류. #305 종료 후 조문객수 - 평형 - status 3스텝 고정. */
type StepKey = "평형" | "조문객수" | "status";

export default function ParlorRequestQuestionStepByStep({
  parlor,
  isVariationB,
  variation = "A",
  showSafeBanner = false,
}: {
  parlor: ParlorDetailProps;
  isVariationB: boolean;
  /** Hackle 실험 #292 (장례식장 견적): A=기존, B=자동진행+버튼 유지, C=자동진행+이전/다음 버튼 제거 */
  variation?: ParlorRequestVariation;
  /** Hackle 실험 #297 (SafeParlor 고이장례연구소 띠지): true면 SafeParlor에 CompanyInfoBanner(띠지) 노출 (B안). SafeParlor에서만 전달. 비용 게이지는 제외 */
  showSafeBanner?: boolean;
}) {
  const isAutoAdvance = variation === "B" || variation === "C";
  const hidePrevButton = variation === "C";
  const hideNextButton = variation === "B" || variation === "C";

  // 비용 게이지는 isVariationB=true(Safe/NotSafe 공통)에서 헤더 아래 sticky로 노출(실험 #296 C안 디폴트 승격).
  const stickyPriceGauge = isVariationB;
  const stickyTopOffset = HEADER_HEIGHT;

  // C variation: 답 선택 후 다음 step의 stepTitle이 sticky 게이지에 가려지지 않도록 스무드 스크롤.
  const stepTitleRef = useRef<HTMLDivElement | null>(null);
  const stickyGaugeRef = useRef<HTMLDivElement | null>(null);

  const [currentStep, setCurrentStep] = useState(1);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingAdvance = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };

  const scheduleAdvance = (fromStep: number) => {
    // 선택 클릭 처리 안에서 동기로 무장한다(setTimeout 안 X)
    if (fromStep === 1) armBackTrap();
    cancelPendingAdvance();
    advanceTimerRef.current = setTimeout(() => {
      // 자동 진행 시점에도 기존 move-next-${n} 발사 (A vs B/C 비교 메트릭 통일)
      // 단 parlor는 기존 move-next 트리거가 trigger_and_target 타입에 없어 path 기존 그대로 + 동일 trigger
      // 분석 단계에서 move-next-${n}을 path "parlor_match_request"로 필터하면 A의 수동 클릭과 동일 메트릭
      customEvent({
        newGtm: { path: "parlor_match_request", trigger_and_target: `move-auto-${fromStep}` },
      });
      setCurrentStep((prev) => prev + 1);
    }, PARLOR_AUTO_ADVANCE_DELAY_MS);
  };

  useEffect(() => () => cancelPendingAdvance(), []);

  // sticky 게이지: currentStep 변경 시 stepTitle이 sticky 게이지 바로 아래에 오도록 스무드 스크롤.
  // 게이지 실제 height를 측정해 모바일/PC 모두 정확한 위치 계산. step1은 첫 진입이라 스킵.
  useEffect(() => {
    if (!stickyPriceGauge) return;
    if (currentStep <= 1) return;
    const timer = setTimeout(() => {
      const titleEl = stepTitleRef.current;
      if (!titleEl) return;
      const gaugeHeight = stickyGaugeRef.current?.getBoundingClientRect().height ?? 0;
      const titleTop = titleEl.getBoundingClientRect().top + window.scrollY;
      const targetScrollY = titleTop - HEADER_HEIGHT - gaugeHeight - 12;
      window.scrollTo({ top: targetScrollY, behavior: "smooth" });
    }, 80);
    return () => clearTimeout(timer);
  }, [currentStep, stickyPriceGauge]);

  const [selectedOption, setSelectedOption] = useReducer<
    Reducer<SelectedOption, Partial<SelectedOption>>
  >(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      장례준비긴급도: "",
      sido: Number(parlor.sido),
      sigungu: Number(parlor.sigungu),
      조문객수: "",
      평형: "",
    },
  );

  // #305 종료 → B(조문객수 First + 빈소 추천) 확정.
  const stepOrder = useMemo<StepKey[]>(() => ["조문객수", "평형", "status"], []);

  const currentStepKey = stepOrder[currentStep - 1];
  const isLastStep = currentStepKey === "status";

  const steps = useMemo(() => stepOrder.map((_, index) => ({ step: index + 1 })), [stepOrder]);

  const stepTitle = useMemo(() => {
    const titleMap: Record<StepKey, string> = {
      평형: "원하시는 빈소 평형을 선택해주세요.",
      조문객수: "예상 조문객 수를\n 선택해주세요",
      status: "장례 준비 상황을\n 자세히 알려주세요",
    };
    return currentStepKey ? titleMap[currentStepKey] : "";
  }, [currentStepKey]);

  // 빈소 평형 추천 (B안에서만 노출). 조문객수 선택값으로 nearest 매핑.
  const binsoOptions = useMemo<BinsoOption[]>(
    () => buildBinsoOptions(parlor.binsos),
    [parlor.binsos],
  );
  const recommendation = useMemo<BinsoRecommendation>(
    () => recommendBinso(selectedOption.조문객수, binsoOptions),
    [selectedOption.조문객수, binsoOptions],
  );

  const isNextDisabled = useMemo(() => {
    if (currentStepKey === "평형") return !selectedOption.평형;
    if (currentStepKey === "조문객수") return !selectedOption.조문객수;
    return !selectedOption.장례준비긴급도;
  }, [selectedOption, currentStepKey]);

  // 평형 step 진입 시 추천 노출/가용 여부 발화 (추천 박스가 실제 보일 때만). #305 B 확정으로 항상 동작.
  useEffect(() => {
    if (currentStepKey !== "평형") return;
    if (!recommendation.label || recommendation.isSingleOption) return;
    customEvent({
      newGtm: { path: "parlor_detail_request", trigger_and_target: "rec_shown" },
    });
    customEvent({
      newGtm: {
        path: "parlor_detail_request",
        trigger_and_target: recommendation.isFallback ? "rec_unavail" : "rec_avail",
      },
    });
  }, [currentStepKey, recommendation]);

  const [loading, setLoading] = useState(false);
  const setResponseData = useSetRecoilState<any>(matchRequestResDataState);

  const { onOpen, onClose } = useDialog();
  const [, setTemporaryMatchRequest] = useRecoilState(temporaryMatchRequestState);

  const isLoggedIn = useRecoilValue(isLoggedInState);

  const setQuotationRequest = useSetRecoilState(quotationRequestState);
  const router = useRouter();
  const { setQuotationRequestCookie, mutateSangjoQuotation } = useSangjoQuotationRequests();

  const 칠곡군농협장례식장_id = "1085";

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    customEvent({
      newGtm: {
        path: "parlor_detail_request",
        trigger_and_target: "submit_conversion",
      },
    });

    if (isNextDisabled) {
      onOpen({
        type: 1,
        closeOnOverlayClick: false,
        header: "응답하지 않은 항목이 있어요",
        children: <>다시 확인해주세요.</>,
        footerProps: {
          text: "확인",
          onClick: onClose,
        },
      });
      return;
    }

    try {
      setLoading(true);

      const body = {
        status: selectedOption.장례준비긴급도,
        funeral_sido: selectedOption.sido,
        funeral_sigungu: selectedOption.sigungu,
        needs_binso: selectedOption.조문객수 === "0명(무빈소)" ? false : true,
        condolers:
          selectedOption.조문객수 === "없음"
            ? ""
            : selectedOption.조문객수 === "0명(무빈소)"
            ? "0명"
            : selectedOption.조문객수,
        is_sequential: false,
      };

      const onSuccess = ({ data }: AxiosResponse<QuotationResponse>) => {
        // 견적 퍼널 완료 지점. outcome 은 니즈 뒷퍼널이 있던 시기의 분류가 남은 것이다
        // (home_question 경로가 사라져 지금은 quotation 하나뿐). 기존 지표 연속성을 위해 유지한다.
        customEvent({
          newGtm: {
            path: "parlor_match_request",
            trigger_and_target: "funnel_done",
            properties: { outcome: "quotation" },
          },
        });

        customEvent({
          newGtm: {
            action: "match_request_complete",
          },
          triggerHackle: true,
        });

        customEvent({
          newGtm: {
            action: "match_request_complete_parlor",
          },
          triggerHackle: true,
        });

        fireGuestSubmitEvents(data?.status);

        if (isLoggedIn) {
          /**
           * `/me`가 붙으면서 결과 페이지가 Quotation id 없이 열린다. 그래서 id를 캐내려고
           * request-complete를 경유하던 이유가 사라졌다. 바로 결과로 보낸다.
           */
          router.push("/match/recommendation/");
        } else {
          setTemporaryMatchRequest(data.id);
          setResponseData(data);
          setQuotationRequest(data);
          setQuotationRequestCookie(data);

          const handleSangjoQuotationRequest = async () => {
            customEvent({
              newGtm: {
                path: "match_request",
                trigger_and_target: "mdl_check_result",
              },
              triggerHackle: true,
            });

            customEvent({
              newGtm: {
                path: "parlor_match_request",
                trigger_and_target: "mdl_check_result",
              },
              triggerHackle: true,
            });

            const anyMemo = getCookie("match_request_memo");
            const previousRequestedQuotation = getCookie(cooKeys.quotation_request);

            const 요청하고_싶은_점 = `${parlor.companyname} 개별 견적 페이지에서 제출한 견적 / 선택한 평형 : ${selectedOption.평형}`;

            const { data } = await Axios.patch(
              `/sangjo_quotation_requests/${previousRequestedQuotation.id}/`,
              {
                // memo 는 STRING(255). 넘기면 BE 가 400 을 내 PATCH 가 실패한다
                memo: (요청하고_싶은_점 + " | " + (anyMemo || "")).slice(0, 255),
              },
            );

            if (!isLoggedIn) {
              setCookie(
                cooKeys.quotation_request,
                JSON.stringify({ ...data, id: previousRequestedQuotation.id }),
              );

              // 로그인 화면이 제출 조건을 되짚어 보여줄 때 쓴다.
              // 평형은 이 폼에서만 받고 서버 body에는 담기지 않아(memo 문자열로만 남는다) 쿠키로 넘긴다
              setCookie(
                cooKeys.parlor_request_info,
                JSON.stringify({
                  parlorName: parlor.companyname,
                  평형: selectedOption.평형,
                }),
              );

              setQuotationRequest(data);

              router.push(
                `/login?redirectTo=${encodeURIComponent(
                  `/match/request/`,
                )}&event_from=uv_parlor_mtch_rqst`,
              );
            } else {
              router.push(
                `/match/request-complete?id=${data.id}&sido=${data.funeral_sido}&sigungu=${data.funeral_sigungu}`,
              );
            }
          };

          handleSangjoQuotationRequest();
          // }
        }
      };

      mutateSangjoQuotation({
        body,
        onSuccess,
        onError: () => {
          setLoading(false);
        },
      });
    } catch (e: any) {
      setLoading(false);
    }
  }, [selectedOption, loading]);

  const handlePageLocation = () => {
    customEvent({
      newGtm: {
        path: "parlor_detail_request",
        trigger_and_target: "CTA",
      },
    });
    const path = { pathname: "/guidebook/", hash: "mainTab" };

    router.push(path);
  };

  const getExpectedPriceOptions = (
    parlor: ParlorDetailProps,
    selectedOption: SelectedOption,
    조문객예상인원_LIST: string[],
  ) => {
    const 빈소평형_LIST = parlor?.binsos
      .map((value, index) => {
        if (index === 4 && value.pyeong) {
          return `${value.pyeong}평 이상`;
        } else if (index < 5 && value.pyeong) {
          return `${value.pyeong}평`;
        } else {
          return;
        }
      })
      .filter((value, index, self) => value !== undefined && self.indexOf(value) === index)
      .concat(["잘 모르겠어요"]);

    const selectedBinsoSizeIndex = selectedOption.평형
      ? 빈소평형_LIST.indexOf(selectedOption.평형)
      : null;

    const selectedCondolerIndex = selectedOption.조문객수
      ? 조문객예상인원_LIST.indexOf(selectedOption.조문객수)
      : null;

    return {
      selectedBinsoSizeIndex,
      selectedCondolerIndex,
      binsoSizeOptions: 빈소평형_LIST as string[],
      condolerOptions: 조문객예상인원_LIST,
    };
  };

  const expectedPriceOptions = useMemo(() => {
    return getExpectedPriceOptions(parlor, selectedOption, 조문객예상인원_LIST);
  }, [parlor, selectedOption]);

  // const isFromAd = "n_query" in router.query || "n_ad" in router.query || "gclid" in router.query;
  const { isOpen, onOpen: onOpenUrgent, onClose: onCloseUrgent } = useDisclosure();

  return (
    <>
      {!isVariationB && <UrgentModal onClose={onCloseUrgent} isOpen={isOpen} />}
      <VStack padding="16px" alignItems="start" css={{ position: "relative" }}>
        <h1 className="head2_b" css={{ whiteSpace: "pre-wrap" }}>
          {String(parlor.id) === 칠곡군농협장례식장_id
            ? `후불제 상조 서비스\n비용 알아보기`
            : `${parlor.companyname}\n장례 비용 알아보기`}
        </h1>
      </VStack>
      {!isVariationB && (
        <HStack alignItems="start" justifyContent="flex-end" margin={"-20px 16px 20px 0"}>
          <RequestQuestionUniqueStepButtons onOpen={onOpenUrgent} />
        </HStack>
      )}
      {/* now 콜 확대 실험 B안은 띠지 자리를 임종 배너로 바꾼다. 띠지가 안 뜨는 SafeParlor(공설·일부 대학병원,
          오인지 유도 가능)에는 임종 배너도 띄우지 않는다. */}
      {(!safeParlorIds.includes(Number(parlor.id)) || showSafeBanner) && (
        <UrgentCallBannerExperiment
          eventPath="parlor_match_request"
          placeName={parlor.companyname}
          padding="0 0 24px"
          fallback={
            <CompanyInfoBanner
              onClick={() => {
                router.push("/funeral-prepare/company-introduction/");
                customEvent({
                  newGtm: {
                    path: "parlor_match_request",
                    trigger_and_target: "uv_compnay_introduction",
                  },
                });
              }}
            />
          }
        />
      )}

      <StepIndicator steps={steps} currentStep={currentStep} />

      <VStack px="16px" pt="24px" style={stickyPriceGauge ? { overflow: "visible" } : undefined}>
        {isVariationB && (
          <div
            ref={stickyGaugeRef}
            style={{
              position: "sticky",
              top: `${stickyTopOffset}px`,
              alignSelf: "stretch",
              zIndex: 5,
              background: colors.white,
              paddingBottom: "8px",
              marginLeft: "-16px",
              marginRight: "-16px",
            }}
          >
            <ExpectedPriceRange
              parlor={parlor}
              expectedPriceOptions={expectedPriceOptions}
              fullBleed
            />
          </div>
        )}

        <Box
          ref={stepTitleRef}
          id="parlor-request-step-title"
          pt="20px"
          width="100%"
          whiteSpace="pre-line"
          className="head2_b black"
        >
          {stepTitle}
        </Box>
        <Box pt="24px" width="100%">
          {isAutoAdvance ? (
            <Box position="relative" width="100%" overflow="hidden">
              <AnimatePresence exitBeforeEnter initial={false}>
                <motion.div
                  key={currentStep}
                  variants={parlorSlideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  style={{ width: "100%" }}
                >
                  <StepQuestion
                    parlor={parlor}
                    currentStep={currentStep}
                    currentStepKey={currentStepKey}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    isAutoAdvance={isAutoAdvance}
                    scheduleAdvance={scheduleAdvance}
                    recommendation={recommendation}
                    binsoOptions={binsoOptions}
                  />
                </motion.div>
              </AnimatePresence>
            </Box>
          ) : (
            <StepQuestion
              parlor={parlor}
              currentStep={currentStep}
              currentStepKey={currentStepKey}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              recommendation={recommendation}
              binsoOptions={binsoOptions}
            />
          )}
        </Box>

        {/* A: 이전+다음 균등 / B: 이전(text)+step3는 다음(작은 filled) / C: step3는 다음만 */}
        <HStack
          pt={hidePrevButton ? "8px" : isAutoAdvance ? 0 : "24px"}
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          gap="24px"
        >
          {!hidePrevButton && (
            <Box width={isAutoAdvance ? "auto" : "100%"}>
              <PrevNext
                disabled={currentStep <= 1}
                text="이전"
                variant={isAutoAdvance ? "text" : "filled"}
                onClick={() => {
                  cancelPendingAdvance();
                  setCurrentStep(currentStep - 1);
                }}
              />
            </Box>
          )}
          <Box
            width={isAutoAdvance && !hidePrevButton ? "auto" : "100%"}
            minWidth={isAutoAdvance && !hidePrevButton ? "200px" : undefined}
            maxWidth={isAutoAdvance && !hidePrevButton ? "45%" : undefined}
          >
            {isLastStep ? (
              <RequestUniqueFixedButton
                handleSubmit={handleSubmit}
                handlePageLocation={handlePageLocation}
                loading={loading}
                isTest
                disabled={isNextDisabled}
              />
            ) : hideNextButton ? null : (
              <PrevNext
                disabled={isNextDisabled}
                text="다음"
                onClick={() => {
                  if (currentStep === 1) armBackTrap();
                  setCurrentStep(currentStep + 1);
                }}
              />
            )}
          </Box>
        </HStack>

        <Box pt="24px" pb="120px" width="100%">
          <StepQuestionExtraDetail currentStepKey={currentStepKey} />
        </Box>
      </VStack>
    </>
  );
}
const PrevNext = ({
  text,
  disabled,
  onClick,
  variant = "filled",
}: {
  text: string;
  disabled?: boolean;
  onClick: () => void;
  variant?: "filled" | "text";
}) => {
  if (variant === "text") {
    return (
      <Button
        onClick={onClick}
        isDisabled={disabled}
        aria-label="이전"
        background="transparent"
        color={colors.gray900}
        fontWeight="400"
        fontSize="15px"
        _hover={{ background: "transparent" }}
        _active={{ background: "transparent", opacity: 0.6 }}
        _disabled={{ color: colors.gray400, background: "transparent" }}
        padding="0 0 0 4px"
        width="auto"
        height="auto"
        minHeight="44px"
        display="inline-flex"
        alignItems="center"
        gap="6px"
      >
        <ColorIcon name="icon-arrow-left" size={18} color="currentColor" aria-hidden />
        {text}
      </Button>
    );
  }

  return (
    <Button
      onClick={onClick}
      isDisabled={disabled}
      background={colors.orange650}
      color="white"
      // hover는 어둡게(orange700). orange500은 비활성색(orange400)보다 창백해 모바일 sticky-hover 시
      // 스텝 이동 직후 같은 위치의 활성 "다음"이 비활성처럼 보이던 문제(557f33ebb 동일 수정, parlor 누락분).
      _hover={{ background: colors.orange700 }}
      _disabled={{
        background: colors.orange400,
      }}
      borderRadius="36px"
      width="100%"
    >
      {text}
    </Button>
  );
};

export interface SelectedOption {
  장례준비긴급도: string;
  조문객수: string;
  평형: string;
  sido: number;
  sigungu: number;
  /** 니즈 첫문항 실험 B안에서 선택한 니즈. A안/비노출 시 "" */
}

const StepQuestion = ({
  currentStep,
  currentStepKey,
  selectedOption,
  setSelectedOption,
  parlor,
  isAutoAdvance = false,
  scheduleAdvance,
  recommendation,
  binsoOptions = [],
}: {
  currentStep: number;
  currentStepKey?: StepKey;
  selectedOption: SelectedOption;
  setSelectedOption: Dispatch<Partial<SelectedOption>>;
  parlor: ParlorDetailProps;
  isAutoAdvance?: boolean;
  scheduleAdvance?: (fromStep: number) => void;
  recommendation?: BinsoRecommendation;
  binsoOptions?: BinsoOption[];
}) => {
  const 빈소평형_LIST = [...binsoOptions.map((option) => option.label), "잘 모르겠어요"];

  if (currentStepKey === "평형") {
    // 조문객수를 먼저 받았을 때 추천 박스 노출 (옵션 1개뿐이면 "추천"이 아니라 유일 옵션이라 숨김). #305 B 확정.
    const showRecommendation =
      Boolean(selectedOption.조문객수) &&
      Boolean(recommendation?.label) &&
      !recommendation?.isSingleOption;

    return (
      <VStack width="100%" minWidth="100%">
        {showRecommendation && (
          <Box
            width="100%"
            mb="12px"
            borderRadius="6px"
            padding="12px"
            background={colors.orange50}
          >
            <Box className="caption_12_b" color={colors.orange650} pb="4px">
              빈소 평형 추천
            </Box>
            {recommendation?.isFallback ? (
              <Box className="body_14_m gray800">조문객이 많은 경우, 가장 큰 빈소를 추천해요.</Box>
            ) : (
              <Box className="body_14_m gray800">
                조문객 {selectedOption.조문객수}라면 보통 이 정도 빈소를 많이 선택해요.
                <br />
                여유있게 조문객을 맞이하시려면 한 단계 큰 빈소도 좋아요.
              </Box>
            )}
          </Box>
        )}
        <Field.ButtonGroups
          items={빈소평형_LIST as string[]}
          value={selectedOption.평형}
          recommendedItem={showRecommendation ? recommendation?.label ?? undefined : undefined}
          onChange={(e) => {
            setSelectedOption({ 평형: e });
            customEvent({
              newGtm: {
                action: "match_request_start",
              },
            });
            customEvent({
              newGtm: {
                path: "parlor_detail_request",
                trigger_and_target: "pyeong-answer",
              },
            });
            // 추천값 그대로 제출했는지 / 다른 평형으로 바꿨는지 (#305 B 확정)
            if (recommendation?.label) {
              customEvent({
                newGtm: {
                  path: "parlor_detail_request",
                  trigger_and_target: e === recommendation.label ? "rec_accept" : "rec_change",
                },
              });
            }
            if (isAutoAdvance && scheduleAdvance) scheduleAdvance(currentStep);
          }}
          borderColor="white"
          buttonWidth="100%"
        />
      </VStack>
    );
  } else if (currentStepKey === "조문객수") {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={조문객예상인원_LIST}
          value={selectedOption.조문객수}
          onChange={(e) => {
            setSelectedOption({ 조문객수: e });

            customEvent({
              newGtm: {
                path: "parlor_match_request",
                trigger_and_target: "condolers-answer",
              },
            });
            customEvent({
              hackle: {
                key: HackleEventName["견적 요청 - 조문객 수 선택"],
                properties: { name: String(e) },
              },
            });
            if (isAutoAdvance && scheduleAdvance) scheduleAdvance(currentStep);
          }}
          borderColor="white"
          buttonWidth="100%"
        />
      </VStack>
    );
  } else if (currentStepKey === "status") {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={장례준비긴급도_LIST}
          value={selectedOption.장례준비긴급도}
          onChange={(e) => {
            setSelectedOption({ 장례준비긴급도: e });

            customEvent({
              newGtm: {
                path: "parlor_match_request",
                trigger_and_target: "status-answer",
              },
            });
            customEvent({
              hackle: {
                key: HackleEventName["견적 요청 - 장례준비긴급도 선택"],
                properties: { name: e },
              },
            });
          }}
          buttonWidth="100%"
          borderColor="white"
        />
      </VStack>
    );
  }

  return null;
};

const StepQuestionExtraDetail = ({ currentStepKey }: { currentStepKey?: StepKey }) => {
  const { openParlorInquiryModal } = useParlorInquiryModal("parlor_match_request");

  if (!currentStepKey) {
    return null;
  }

  if (currentStepKey === "평형") {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box className="subtitle_14_b gray600" width="100%">
          <span className="caption_12_b gray500">
            e하늘 장사정보시스템 공공 데이터를 통해 고이장례연구소에서 장례식장 예상비용을 자체
            산정해요.
          </span>
        </Box>
      </VStack>
    );
  } else if (currentStepKey === "조문객수") {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box className="subtitle_14_b gray600" width="100%">
          <span className="caption_12_b gray500">
            조문객 수에 따라 전체 장례 비용을 예상할 수 있어요.
          </span>
        </Box>
      </VStack>
    );
  } else if (currentStepKey === "status") {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box width="100%" className="body_14_m gray800" pb="36px">
          장례 비용 뿐만 아니라 전반적인 장례 절차, 임종접수, 긴급한 문의가 있으시면
          고이장례연구소로 부담없이 전화나 채팅으로 연락주세요.
        </Box>
        <HStack width="100%">
          <Button
            flexGrow={1}
            style={{
              borderRadius: 6,
              backgroundColor: colors.gray800,
              padding: "6px 14px",
              boxShadow: "2px 2px 2px #00000022",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "parlor_match_request",
                  trigger_and_target: "quick_cnslt-opn_urgnt_mdl",
                },
              });
              void openParlorInquiryModal();
            }}
          >
            <HStack alignItems="center" justifyContent="center" gap={1}>
              <ColorIcon name="icon-call-mono" color={colors.white} width={12} height={12} />
              <div className="caption_12_sb white">전화 상담</div>{" "}
            </HStack>
          </Button>
          <Button
            flexGrow={1}
            style={{
              borderRadius: 8,
              backgroundColor: colors.gray800,
              padding: "6px 14px",
              boxShadow: "2px 2px 2px #00000022",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "parlor_match_request",
                  trigger_and_target: "chat",
                },
              });
              window.open("http://pf.kakao.com/_xgFDaK/chat");
            }}
          >
            <HStack alignItems="center" justifyContent="center" gap={1}>
              <ColorIcon name="chat_bubble_rounded" color={colors.white} width={12} height={12} />
              <div className="caption_12_sb white">채팅 상담</div>{" "}
            </HStack>
          </Button>
        </HStack>
      </VStack>
    );
  } else return null;
};

const StepIndicator = ({ steps, currentStep }: { steps: any[]; currentStep: number }) => {
  // 마지막 하나는 항상 여유(로그인/결과 단계) - 질문 단계에선 절대 채워지지 않는 dot 하나를 더 붙인다.
  const totalDots = steps.length + 1;
  return (
    <HStack width="100%" alignItems="center" justifyContent="center" gap={1} paddingRight="6px">
      {Array.from({ length: totalDots }).map((_, index) => (
        <Box
          key={index}
          width="8px"
          height="8px"
          borderRadius="50%"
          backgroundColor={currentStep === index + 1 ? colors.gray900 : colors.gray400}
        ></Box>
      ))}
    </HStack>
  );
};

interface ExpectedPriceRangeProps {
  parlor: ParlorDetailProps;
  expectedPriceOptions: {
    selectedBinsoSizeIndex: number | null;
    selectedCondolerIndex: number | null;
    binsoSizeOptions: string[];
    condolerOptions: string[];
  };
  fullBleed?: boolean;
}

const ExpectedPriceRange = ({
  parlor,
  expectedPriceOptions,
  fullBleed = false,
}: ExpectedPriceRangeProps) => {
  const MIN_PRICE = Math.floor(parlor.expected_charge_sum["무빈소"] / 10000);
  const MAX_PRICE = Math.floor(parlor.expected_charge_sum["일반장"] / 10000);
  const RANGE = MAX_PRICE - MIN_PRICE;

  const { selectedBinsoSizeIndex, selectedCondolerIndex, binsoSizeOptions, condolerOptions } =
    expectedPriceOptions;

  const condolerRangeMap: Record<number, { leftOffset: number; rightOffset: number }> = {
    0: { leftOffset: 30, rightOffset: 30 },
    1: { leftOffset: 50, rightOffset: 100 },
    2: { leftOffset: 80, rightOffset: 150 },
    3: { leftOffset: 100, rightOffset: 200 },
  };

  const isUnknownBinsoSize =
    selectedBinsoSizeIndex !== null &&
    binsoSizeOptions[selectedBinsoSizeIndex]?.includes("잘 모르겠어요");
  const isUnknownCondoler =
    selectedCondolerIndex && condolerOptions[selectedCondolerIndex]?.includes("모르겠어요");

  const calculatePriceRange = () => {
    if (selectedBinsoSizeIndex === null) {
      // B안(조문객수 우선): 평형을 아직 안 골랐어도 조문객수를 골랐으면 게이지가 반응하도록.
      // A안에서 첫 문항(평형) 선택 시 게이지가 변하는 것과 경험을 일치시킨다 (실험 공정성).
      // 평형이 없어 절대 기준점이 없으므로 중간값(RANGE x 0.5)을 base로 조문객수 range만 적용.
      if (selectedCondolerIndex !== null && !isUnknownCondoler) {
        if (selectedCondolerIndex === 0) {
          return { min: MIN_PRICE, max: MIN_PRICE + Math.floor(RANGE * 0.15) };
        }
        const midBase = MIN_PRICE + Math.floor(RANGE * 0.5);
        const condolerRange = condolerRangeMap[selectedCondolerIndex];
        return {
          min: Math.max(MIN_PRICE, midBase - condolerRange.leftOffset),
          max: Math.min(MAX_PRICE, midBase + condolerRange.rightOffset),
        };
      }
      return { min: MIN_PRICE, max: MAX_PRICE };
    }

    if (isUnknownBinsoSize && selectedCondolerIndex === null) {
      return { min: MIN_PRICE, max: MAX_PRICE };
    }

    /**
     * 빈소평형 개수에 따라, 레인지를 N등분
     * (예시)
     * - selectedBinsoSizeIndex = 2, binsoSizeOptions.length = 5
     * - baseBinsoPriceRatio = 0.6 (60%)
     * - 즉, 3번째 점이 기준점이 됨 = [  |  |  _|_ |  ]
     * - "잘 모르겠어요" 선택 시, 중간값(50%)을 기준점으로 사용
     *  */
    let baseBinsoPriceRatio: number;
    if (isUnknownBinsoSize) {
      baseBinsoPriceRatio = 0.5; // 중간값
    } else {
      baseBinsoPriceRatio = (selectedBinsoSizeIndex + 1) / binsoSizeOptions.length;
    }
    const baseBinsoPrice = MIN_PRICE + Math.floor(RANGE * baseBinsoPriceRatio);

    // 무빈소 선택
    if (selectedCondolerIndex === 0) {
      return {
        min: MIN_PRICE,
        max: MIN_PRICE + Math.floor(RANGE * 0.15),
      };
    }

    if (selectedCondolerIndex === null) {
      return {
        min: Math.max(MIN_PRICE, baseBinsoPrice - 100),
        max: Math.min(MAX_PRICE, baseBinsoPrice + 150),
      };
    }

    if (isUnknownCondoler) {
      return {
        min: Math.max(MIN_PRICE, baseBinsoPrice - 100),
        max: Math.min(MAX_PRICE, baseBinsoPrice + 150),
      };
    }

    const condolerRange = condolerRangeMap[selectedCondolerIndex];

    return {
      min: Math.max(MIN_PRICE, baseBinsoPrice - condolerRange.leftOffset),
      max: Math.min(MAX_PRICE, baseBinsoPrice + condolerRange.rightOffset),
    };
  };

  const { min, max } = calculatePriceRange();

  const gaugeProps = { min, max, MIN_PRICE, MAX_PRICE, fullBleed };

  // #303 종료 → A(막대형 게이지) 확정.
  return <ExpectedPriceRangeBar {...gaugeProps} />;
};
